import { useEffect, useState } from 'react'
import { getCaixaDoMes, type CaixaDoMes } from '@services/gestao.js'
import { CaixaMesStrip } from '@components/obra/CaixaMesStrip.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { Select } from '@components/primitives/Select.js'
import { CATEGORIAS_FINANCEIRAS, MODULOS } from '@utils/constants.js'
import { formatCurrency } from '@utils/format.js'
import { getPeriodoStart, type LancamentoItem, type PeriodoFiltro } from './financeiroTypes.js'
import { DashboardCharts } from './DashboardCharts.js'
import { CurvaDesembolso } from './CurvaDesembolso.js'

type Props = {
  lancamentos: LancamentoItem[]
  orcamentoTotal: number
  orcamentoPorCategoria: Record<string, number>
}

const HEALTH = {
  saudavel: { label: 'Saudável', cls: 'cf3-fin-dash__health--saudavel' },
  atencao: { label: 'Atenção', cls: 'cf3-fin-dash__health--atencao' },
  critico: { label: 'Crítico', cls: 'cf3-fin-dash__health--critico' },
}

export function FinanceiroDashboard({ lancamentos, orcamentoTotal, orcamentoPorCategoria }: Props) {
  const [periodo, setPeriodo] = useState<PeriodoFiltro>('total')
  const [catFiltro, setCatFiltro] = useState('')
  const [moduloFiltro, setModuloFiltro] = useState('')
  const [caixa, setCaixa] = useState<CaixaDoMes | null>(null)

  useEffect(() => {
    void getCaixaDoMes().then(setCaixa)
  }, [lancamentos])

  const startDate = getPeriodoStart(periodo)
  const gastos = lancamentos.filter((l) => {
    if (l.tipo === 'receita') return false
    if (startDate && l.data < startDate) return false
    if (catFiltro && l.categoria !== catFiltro) return false
    if (moduloFiltro && l.moduloId !== moduloFiltro) return false
    return true
  })

  const totalGasto = gastos.filter((l) => l.status === 'pago').reduce((s, l) => s + l.valor, 0)
  const totalPrevisto = gastos.reduce((s, l) => s + l.valor, 0)
  const saldo = orcamentoTotal - totalGasto
  const pct = orcamentoTotal > 0 ? Math.min((totalGasto / orcamentoTotal) * 100, 100) : 0
  const healthStatus = pct >= 90 ? 'critico' : pct >= 70 ? 'atencao' : 'saudavel'

  const gastoPorCat: Record<string, number> = {}
  for (const l of gastos) {
    gastoPorCat[l.categoria] = (gastoPorCat[l.categoria] ?? 0) + l.valor
  }

  const allCats = new Set([...Object.keys(gastoPorCat), ...Object.keys(orcamentoPorCategoria)])
  const barData = [...allCats]
    .filter((c) => (gastoPorCat[c] ?? 0) > 0 || (orcamentoPorCategoria[c] ?? 0) > 0)
    .map((c) => ({
      cat: c.length > 14 ? c.slice(0, 13) + '…' : c,
      realizado: gastoPorCat[c] ?? 0,
      previsto: orcamentoPorCategoria[c] ?? 0,
    }))
    .sort((a, b) => b.realizado - a.realizado)

  const pieData = Object.entries(gastoPorCat)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)

  const top10 = [...gastos].sort((a, b) => b.valor - a.valor).slice(0, 10)

  const byFornecedor: Record<string, number> = {}
  for (const l of gastos) {
    if (l.fornecedor) byFornecedor[l.fornecedor] = (byFornecedor[l.fornecedor] ?? 0) + l.valor
  }
  const fornList = Object.entries(byFornecedor).sort(([, a], [, b]) => b - a)

  const catAlerts = Object.entries(gastoPorCat)
    .filter(([cat, v]) => {
      const budget = orcamentoPorCategoria[cat]
      return budget && v / budget >= 0.8
    })
    .map(([cat, v]) => ({
      cat,
      pct: Math.round((v / orcamentoPorCategoria[cat]!) * 100),
    }))

  const catOptions = [
    { value: '', label: 'Todas as categorias' },
    ...CATEGORIAS_FINANCEIRAS.map((c) => ({ value: c, label: c })),
  ]
  const moduloOptions = [
    { value: '', label: 'Todos os módulos' },
    ...MODULOS.map((m) => ({ value: m.id, label: m.titulo })),
  ]

  return (
    <div className='cf3-fin-dash'>
      <div className='cf3-fin-dash__filters'>
        <div className='cf3-chip-row' role='group' aria-label='Período'>
          {(
            [
              ['total', 'Todo período'],
              ['mes', 'Mês atual'],
              ['trimestre', 'Últ. 3 meses'],
            ] as const
          ).map(([val, label]) => (
            <button
              key={val}
              className={`cf3-chip${periodo === val ? ' cf3-chip--active' : ''}`}
              onClick={() => setPeriodo(val)}
              aria-pressed={periodo === val}
            >
              {label}
            </button>
          ))}
        </div>
        <div className='cf3-fin-dash__filter-row'>
          <Select
            name='dash-cat'
            value={catFiltro}
            options={catOptions}
            onChange={(e) => setCatFiltro(e.target.value)}
          />
          <Select
            name='dash-modulo'
            value={moduloFiltro}
            options={moduloOptions}
            onChange={(e) => setModuloFiltro(e.target.value)}
          />
        </div>
      </div>

      {orcamentoTotal > 0 && totalGasto > orcamentoTotal && (
        <div className='cf3-fin-dash__alert cf3-fin-dash__alert--danger' role='alert'>
          <strong>Orçamento total ultrapassado!</strong> Gasto ({formatCurrency(totalGasto)}) excede
          o orçamento ({formatCurrency(orcamentoTotal)}).
        </div>
      )}
      {caixa && <CaixaMesStrip caixa={caixa} compact />}

      {catAlerts.map(({ cat, pct: p }) => (
        <div
          key={cat}
          className={`cf3-fin-dash__alert${p >= 100 ? ' cf3-fin-dash__alert--danger' : ' cf3-fin-dash__alert--warning'}`}
          role='alert'
        >
          <strong>{cat}</strong>: {p}% do orçamento previsto{' '}
          {p >= 100 ? 'ultrapassado' : 'utilizado'}.
        </div>
      ))}

      <div className={`cf3-fin-dash__health ${HEALTH[healthStatus].cls}`}>
        <div className='cf3-fin-dash__health-status'>{HEALTH[healthStatus].label}</div>
        <div
          className='cf3-fin-dash__health-pct'
          aria-label={`${Math.round(pct)}% do orçamento utilizado`}
        >
          {Math.round(pct)}
          <span className='cf3-fin-dash__health-pct-unit'>%</span>
        </div>
        <div className='cf3-fin-dash__health-sub'>do orçamento total utilizado</div>
        <div className='cf3-fin-dash__health-cards'>
          <div className='cf3-fin-dash__hcard'>
            <span className='cf3-fin-dash__hcard-label'>Orçamento</span>
            <strong className='cf3-fin-dash__hcard-val'>{formatCurrency(orcamentoTotal)}</strong>
          </div>
          <div className='cf3-fin-dash__hcard'>
            <span className='cf3-fin-dash__hcard-label'>Realizado</span>
            <strong className='cf3-fin-dash__hcard-val'>{formatCurrency(totalGasto)}</strong>
          </div>
          <div className='cf3-fin-dash__hcard'>
            <span className='cf3-fin-dash__hcard-label'>Saldo</span>
            <strong
              className={`cf3-fin-dash__hcard-val${saldo < 0 ? ' cf3-fin-dash__hcard-val--danger' : ' cf3-fin-dash__hcard-val--ok'}`}
            >
              {formatCurrency(saldo)}
            </strong>
          </div>
          <div className='cf3-fin-dash__hcard'>
            <span className='cf3-fin-dash__hcard-label'>Lançado</span>
            <strong className='cf3-fin-dash__hcard-val'>{formatCurrency(totalPrevisto)}</strong>
          </div>
        </div>
      </div>

      {gastos.length === 0 ? (
        <EmptyPanel description='Nenhum lançamento no período selecionado.' />
      ) : (
        <>
          <DashboardCharts barData={barData} pieData={pieData} top10={top10} fornList={fornList} />
          <CurvaDesembolso lancamentos={gastos} />
        </>
      )}
    </div>
  )
}
